import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Theater, MapPin, BadgeCheck, Share2, Users, Ruler, Loader2,
  Calendar, ArrowRight, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import HallAvailabilityCalendar from "@/components/HallAvailabilityCalendar";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#7C3AED";

/**
 * Public profile for L18 Theater Hall at /theater-hall/:handle.
 * Shows facility spec + rate card + a 6-month availability calendar.
 * No auth required — shareable to producers & touring groups.
 */
export default function PublicTheaterHall() {
  const { handle } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [stageCatalog, setStageCatalog] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/theater-halls/${handle}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Hall not found."));
    axios.get(`${API}/meta/theater-hall-stage-types`)
      .then(({ data }) => setStageCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [handle]);

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/theater-hall/${handle}`
    : "";

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: data?.hall?.name || "Theater Hall",
          text: `Book ${data?.hall?.name} on Call·Sheet`,
          url: shareUrl,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard.");
    } catch { /* dismissed */ }
  };

  if (err) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-20 text-center"
           data-testid="public-theater-hall-error">
        <Theater size={40} className="mx-auto text-white/25 mb-4"/>
        <div className="text-white/70 text-lg">{err}</div>
        <Link to="/theater/halls"
              className="mt-6 inline-block text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-white underline underline-offset-4">
          Browse other halls
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-20 text-center text-white/50"
           data-testid="public-theater-hall-loading">
        <Loader2 size={16} className="animate-spin inline mr-2"/> loading…
      </div>
    );
  }

  const { hall, availability, window: win } = data;
  const stageLabel = stageCatalog.find((s) => s.key === hall.stage_type)?.label || hall.stage_type || "—";

  const onDayClick = (iso, slots) => {
    // Route to the discovery page and pre-open the booking modal via
    // the login/register gate. Anonymous visitors need to sign in first.
    nav(`/theater/halls?city=${encodeURIComponent(hall.city || "")}&preferred_hall=${hall.id}&preferred_date=${iso}`);
  };

  return (
    <div className="min-h-screen" data-testid="public-theater-hall">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/8">
        {hall.hero_image_url && (
          <img src={hall.hero_image_url} alt=""
               className="absolute inset-0 w-full h-full object-cover opacity-25"/>
        )}
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at top, ${ACCENT}22 0%, transparent 60%)`,
        }}/>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            L18 · Theater Hall
          </div>
          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight"
                style={{ color: ACCENT }}
                data-testid="public-th-name">
              {hall.name}
            </h1>
            <Button onClick={share}
                    className="h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white"
                    data-testid="public-th-share">
              <Share2 size={12} className="mr-2"/> Share
            </Button>
          </div>
          <div className="mt-3 text-sm text-white/60 flex items-center gap-3 flex-wrap">
            {hall.legal_name && hall.legal_name !== hall.name && <span>{hall.legal_name}</span>}
            {hall.verified && (
              <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
                <BadgeCheck size={10} className="inline mr-1"/> Verified
              </span>
            )}
            {hall.city && (
              <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
                <MapPin size={10} className="inline mr-1"/> {hall.city}
                {hall.state && `, ${hall.state}`}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase"
                  style={{ background: `${ACCENT}22`, color: ACCENT }}
                  data-testid="public-th-stage">
              {stageLabel}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
              <Users size={10} className="inline mr-1"/> {hall.seats} seats
            </span>
          </div>

          {hall.bio && (
            <p className="mt-6 max-w-3xl text-[15px] text-white/75 leading-relaxed"
               data-testid="public-th-bio">
              {hall.bio}
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* CALENDAR */}
        <section data-testid="public-th-calendar-section">
          <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white/95 mb-5 flex items-center gap-2.5">
            <Calendar size={20} style={{ color: ACCENT }}/> Availability
          </h2>
          <HallAvailabilityCalendar
            availability={availability}
            blackout={hall.blackout_dates || []}
            window={win}
            onDayClick={onDayClick}
            accent={ACCENT}
          />
          <p className="mt-3 text-[11px] text-white/45 max-w-2xl leading-relaxed">
            Tap any free day to submit a booking request. Sign in as a theater group to request a specific slot and price.
          </p>
        </section>

        {/* FACILITY SPEC */}
        <section data-testid="public-th-facility-section">
          <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white/95 mb-5 flex items-center gap-2.5">
            <Building2 size={20} style={{ color: ACCENT }}/> Facility
          </h2>
          <div className="grid sm:grid-cols-4 gap-3">
            <SpecStat label="Stage Width" value={`${hall.stage_width || 0} m`} accent={ACCENT}/>
            <SpecStat label="Stage Depth" value={`${hall.stage_depth || 0} m`} accent={ACCENT}/>
            <SpecStat label="Stage Height" value={`${hall.stage_height || 0} m`} accent={ACCENT}/>
            <SpecStat label="Seats" value={hall.seats} accent={ACCENT}/>
          </div>
          {hall.tech_specs && (
            <div className="mt-5 cs-card rounded-xl p-5" data-testid="public-th-tech-specs">
              <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">Tech Rider</div>
              <p className="text-[13px] text-white/75 leading-relaxed whitespace-pre-wrap">
                {hall.tech_specs}
              </p>
            </div>
          )}
        </section>

        {/* RATE CARD */}
        {hall.rate_card && (hall.rate_card.evening || hall.rate_card.matinee) ? (
          <section data-testid="public-th-rates-section">
            <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white/95 mb-5">
              Rate Card
            </h2>
            <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
              {hall.rate_card.matinee > 0 && (
                <RateCard label="Matinee Slot" price={hall.rate_card.matinee}
                          currency={hall.rate_card.currency || "INR"} accent={ACCENT}/>
              )}
              {hall.rate_card.evening > 0 && (
                <RateCard label="Evening Slot" price={hall.rate_card.evening}
                          currency={hall.rate_card.currency || "INR"} accent={ACCENT}/>
              )}
            </div>
            <p className="mt-3 text-[11px] text-white/45 leading-relaxed">
              Listed rates are indicative. Weekend and premium-date rates may vary — negotiate directly with the venue.
            </p>
          </section>
        ) : null}

        {/* FOOTER CTA */}
        <div className="pt-8 border-t border-white/8 flex flex-wrap items-center justify-between gap-4">
          <div className="text-[11px] text-white/45 tracking-[0.2em] uppercase">
            Powered by Call·Sheet
          </div>
          <Link to="/register" className="text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-white underline underline-offset-4"
                data-testid="public-th-cta-register">
            Book this hall <ArrowRight size={11} className="inline ml-1"/>
          </Link>
        </div>
      </div>
    </div>
  );
}

function SpecStat({ label, value, accent }) {
  return (
    <div className="cs-card rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/45">
        <Ruler size={10} style={{ color: accent }}/> {label}
      </div>
      <div className="mt-1.5 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function RateCard({ label, price, currency, accent }) {
  const symbol = currency === "INR" ? "₹" : (currency + " ");
  return (
    <div className="cs-card rounded-xl p-5">
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
      <div className="mt-2 font-display text-3xl tracking-tight"
           style={{ color: accent }}>
        {symbol}{(price || 0).toLocaleString()}
      </div>
      <div className="mt-0.5 text-[10px] tracking-[0.16em] uppercase text-white/45">
        per show
      </div>
    </div>
  );
}
